// src/App.tsx
import React, { createContext, useContext, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { CssBaseline, Box } from '@mui/material';
import { LocaleProvider } from './contexts/LocaleContext';
import { AppThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationsProvider } from './hooks/useNotifications';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { HeaderDrawerProvider } from './contexts/HeaderDrawerContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import BeingsListPage from './pages/BeingsListPage';
import BeingsPage from './pages/BeingsPage';
import BookingsListPage from './pages/BookingsListPage';
import BookingPage from './pages/BookingPage';
import KennelsPage from './pages/KennelsPage';
import CalendarPage from './pages/CalendarPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ToastTestPage from './pages/ToastTestPage';
import ErrorTestPage from './pages/ErrorTestPage';
import ScrollResetWrapper from './components/ScrollResetWrapper';
import PerformanceIndicator from './components/PerformanceIndicator';
import ResolutionIndicator from './components/ResolutionIndicator';
import { setupAxiosInterceptors } from './utils/axiosInterceptor';
import { useToast } from './hooks/useToast';
import { useNotificationToast } from './hooks/useNotificationToast';
import { useNotificationSocket } from './hooks/useNotificationSocket';
import { useWebVitals } from './hooks/useWebVitals';
import ToastMessage from './components/common/ToastMessage';
import NotificationToast from './components/notifications/NotificationToast';
import ErrorBoundary from './errors/ErrorBoundary';
import { ErrorProvider } from './errors/ErrorContext';
import ErrorLogsPage from './pages/ErrorLogsPage';
import TodaysPage from './pages/TodaysPage';
import PerformanceDashboard from './pages/PerformanceDashboard';
import ActivityLogPage from './pages/ActivityLogPage';
import QuotePage from './pages/QuotePage';
import BookingsAnalyticsPage from './pages/BookingsAnalyticsPage';
import ProfilePage from './pages/ProfilePage';

interface ToastContextValue {
  showSuccess: (message?: string) => void;
  showError: (message?: string) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const toastHook = useToast();

  return (
    <ToastContext.Provider value={toastHook}>
      {children}
      <ToastMessage
        disableAutoClose={false}
        show={toastHook.toast.show}
        type={toastHook.toast.type}
        message={toastHook.toast.message}
        onClose={toastHook.hideToast}
      />
      <NotificationBridge />
    </ToastContext.Provider>
  );
};

export const useGlobalToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useGlobalToast must be used within a ToastProvider');
  }
  return context;
};

// Bridges the SignalR connection to the notification-toast queue. Kept as
// its own small component (rather than inlined in ToastProvider) so the
// socket connection only mounts once, inside the same provider nesting
// that already gives it access to useAuth() further up the tree.
const NotificationBridge: React.FC = () => {
  const { current, enqueue, dismiss, markAsRead } = useNotificationToast();
  useNotificationSocket(enqueue);

  return (
    <NotificationToast
      notification={current}
      onClose={dismiss}
      onMarkAsRead={markAsRead}
    />
  );
};

const ConditionalHeader: React.FC = () => {
  const location = useLocation();
  if (
    ['/login', '/forgot-password', '/reset-password'].includes(
      location.pathname
    )
  )
    return null;
  return <Header />;
};

const App: React.FC = () => {
  useWebVitals();

  useEffect(() => {
    document.title = 'Villa Siesta Pet Retreat';
    setupAxiosInterceptors();
  }, []);

  return (
    <AuthProvider>
      <LocaleProvider>
        <AppThemeProvider>
          <CssBaseline />
          <Router>
            <NotificationsProvider>
              <AccessibilityProvider>
                <ErrorProvider>
                  <ToastProvider>
                    <HeaderDrawerProvider>
                      <ErrorBoundary>
                        <ScrollResetWrapper>
                          <ConditionalHeader />
                          <PerformanceIndicator />
                          <ResolutionIndicator />
                          <Box
                            className="mainPageBackground"
                            sx={{
                              flex: 1,
                              backgroundColor: (theme) =>
                                theme.palette.background.default,
                            }}
                          >
                            <Routes>
                              <Route path="/login" element={<LoginPage />} />
                              <Route
                                path="/forgot-password"
                                element={<ForgotPasswordPage />}
                              />
                              <Route
                                path="/reset-password"
                                element={<ResetPasswordPage />}
                              />

                              {/* All authenticated users */}
                              <Route
                                path="/"
                                element={
                                  <ProtectedRoute>
                                    <HomePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/today/*"
                                element={
                                  <ProtectedRoute>
                                    <TodaysPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/kennels"
                                element={
                                  <ProtectedRoute>
                                    <KennelsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/calendar"
                                element={
                                  <ProtectedRoute>
                                    <CalendarPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Bookings - all authenticated can view; only Admin/Manager can create/edit.
                              key="bookings" / key="quotes" forces a fresh mount when toggling between
                              the two BookingsListPage variants so useBookingsFilter's lazy state
                              initialiser re-runs with the correct statusPreset. */}
                              <Route
                                path="/bookings"
                                element={
                                  <ProtectedRoute>
                                    <BookingsListPage key="bookings" />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/booking/:id"
                                element={
                                  <ProtectedRoute>
                                    <BookingPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/booking/:id/edit"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BookingPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/quote/:id"
                                element={
                                  <ProtectedRoute>
                                    <QuotePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/quote/:id/edit"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <QuotePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/clients/:clientId/newbooking"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BookingPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Quotes */}
                              <Route
                                path="/quote/new"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <QuotePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/clients/:clientId/quote/new"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <QuotePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/quotes"
                                element={
                                  <ProtectedRoute>
                                    <BookingsListPage
                                      key="quotes"
                                      statusPreset="Quotation"
                                    />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Clients/Pets - Admin and Manager only */}
                              <Route
                                path="/clients"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BeingsListPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/pets"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BeingsListPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/clients/new"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BeingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/clients/:id/*"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BeingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/pets/:id/*"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BeingsPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Settings - Admin and Manager */}
                              <Route
                                path="/settings"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <SettingsPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Admin only */}
                              <Route
                                path="/admin/error-logs"
                                element={
                                  <ProtectedRoute allowRoles={['Admin']}>
                                    <ErrorLogsPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Admin/Manager tools */}
                              <Route
                                path="/admin/performance-dashboard"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <PerformanceDashboard />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/admin/activity-log"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <ActivityLogPage />
                                  </ProtectedRoute>
                                }
                              />

                              <Route
                                path="/admin/bookings-analytics"
                                element={
                                  <ProtectedRoute
                                    allowRoles={['Admin', 'Manager']}
                                  >
                                    <BookingsAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Profile and notifications */}
                              <Route
                                path="/profile"
                                element={
                                  <ProtectedRoute>
                                    <ProfilePage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Dev/test routes */}
                              <Route
                                path="/toast-test"
                                element={
                                  <ProtectedRoute>
                                    <ToastTestPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/error-test"
                                element={
                                  <ProtectedRoute>
                                    <ErrorTestPage />
                                  </ProtectedRoute>
                                }
                              />
                            </Routes>
                          </Box>
                        </ScrollResetWrapper>
                      </ErrorBoundary>
                    </HeaderDrawerProvider>
                  </ToastProvider>
                </ErrorProvider>
              </AccessibilityProvider>
            </NotificationsProvider>
          </Router>
        </AppThemeProvider>
      </LocaleProvider>
    </AuthProvider>
  );
};

export default App;
